import React, { useState } from 'react';

const RemoveDuplicateLines: React.FC = () => {
  const [text, setText] = useState('');
  const [options, setOptions] = useState({
    caseSensitive: true,
    trimWhitespace: true,
    removeEmpty: false,
    keepFirst: true
  });

  const removeDuplicates = () => {
    if (!text) return '';

    let lines = text.split('\n');
    
    // Process lines based on options
    const processedLines = lines.map(line => {
      if (options.trimWhitespace) {
        return line.trim();
      }
      return line;
    });

    // Remove empty lines if option is selected
    const filteredLines = options.removeEmpty 
      ? processedLines.filter(line => line.length > 0)
      : processedLines;

    // Remove duplicates
    const seen = new Set<string>();
    const uniqueLines: string[] = [];

    filteredLines.forEach(line => {
      const compareText = options.caseSensitive ? line : line.toLowerCase();
      
      if (!seen.has(compareText)) {
        seen.add(compareText);
        uniqueLines.push(line);
      } else if (!options.keepFirst) {
        // If keepFirst is false, we want to keep the last occurrence
        // Remove the previous occurrence and add this one
        const originalIndex = uniqueLines.findIndex(existingLine => 
          options.caseSensitive 
            ? existingLine === line 
            : existingLine.toLowerCase() === line.toLowerCase()
        );
        if (originalIndex !== -1) {
          uniqueLines.splice(originalIndex, 1);
          uniqueLines.push(line);
        }
      }
    });

    return uniqueLines.join('\n');
  };

  const processedText = removeDuplicates();

  const getStats = () => {
    const originalLines = text ? text.split('\n') : [];
    const processedLines = processedText ? processedText.split('\n') : [];
    const duplicatesRemoved = originalLines.length - processedLines.length;

    return {
      originalCount: originalLines.length,
      uniqueCount: processedLines.length,
      duplicatesRemoved: Math.max(0, duplicatesRemoved)
    };
  };

  const stats = getStats();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(processedText);
  };

  const clearText = () => {
    setText('');
  };

  const toggleOption = (key: keyof typeof options) => {
    setOptions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Remove Duplicate Lines</h1>
        <p className="text-gray-600">Remove duplicate lines from your text with various options</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Enter Your Text</h3>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter text with duplicate lines..."
              className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <div className="mt-4 flex gap-2">
              <button 
                onClick={clearText}
                disabled={!text}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 transition-colors"
              >
                Clear Text
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Options</h3>
            <div className="space-y-4">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.caseSensitive}
                  onChange={() => toggleOption('caseSensitive')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Case Sensitive</span>
                  <p className="text-xs text-gray-500">Treat "Hello" and "hello" as different</p>
                </div>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.trimWhitespace}
                  onChange={() => toggleOption('trimWhitespace')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Trim Whitespace</span>
                  <p className="text-xs text-gray-500">Remove leading and trailing spaces before comparison</p>
                </div>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.removeEmpty}
                  onChange={() => toggleOption('removeEmpty')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Remove Empty Lines</span>
                  <p className="text-xs text-gray-500">Remove blank lines from the result</p>
                </div>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.keepFirst}
                  onChange={() => toggleOption('keepFirst')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Keep First Occurrence</span>
                  <p className="text-xs text-gray-500">Keep the first occurrence of duplicates (otherwise keep last)</p>
                </div>
              </label>
            </div>

            {text && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Statistics</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Original lines:</span>
                    <span className="font-medium">{stats.originalCount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Unique lines:</span>
                    <span className="font-medium text-green-600">{stats.uniqueCount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Duplicates removed:</span>
                    <span className="font-medium text-red-600">{stats.duplicatesRemoved}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Unique Lines</h3>
              <button
                onClick={copyToClipboard}
                disabled={!processedText}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300 transition-colors"
              >
                Copy Result
              </button>
            </div>
            <textarea
              value={processedText}
              readOnly
              className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50"
              placeholder="Text with duplicates removed will appear here..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoveDuplicateLines;
